/**
 * Persona Service - Handles API interactions for agent personas and profiles
 */

import axios from 'axios';
import { post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/mms_persistence/persona`;

/**
 * Get all personas with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of personas
 */
export const getPersonas = async (filters = {},sort={},query =null) => {
  try {
    const response = await post(`${API_URL}/fetch?query=${query}`, { filter: filters,sort: sort });
    return response;
  } catch (error) {
    console.error('Error fetching personas:', error);
    throw error;
  }
};

/**
 * Get a specific persona by ID
 * @param {string} personaId - Persona ID
 * @returns {Promise<Object>} Promise resolving to persona object
 */
export const getPersonaById = async (personaId) => {
  try {
    const response = await axios.get(`${API_URL}/${personaId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Create a new persona
 * @param {Object} personaData - Persona data
 * @returns {Promise<Object>} Promise resolving to created persona
 */
export const createPersona = async (personaData) => {
  try {
    const response = await post(API_URL, personaData);
    return response.data;
  } catch (error) {
    console.error('Error creating persona:', error);
    throw error;
  }
};

/**
 * Update an existing persona
 * @param {string} personaId - Persona ID
 * @param {Object} personaData - Updated persona data
 * @returns {Promise<Object>} Promise resolving to updated persona
 */
export const updatePersona = async (personaData) => {
  try {
    const response = await put(`${API_URL}`, personaData);
    return response;
  } catch (error) {
    console.error(`Error updating persona :`, error);
    throw error;
  }
};

/**
 * Delete a persona
 * @param {string} personaId - Persona ID
 * @returns {Promise<void>} Promise resolving when persona is deleted
 */
export const deletePersona = async (personaId) => {
  try {
    await axios.delete(`${API_URL}/${personaId}`);
  } catch (error) {
    console.error(`Error deleting persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona capabilities
 * @param {string} personaId - Persona ID
 * @returns {Promise<Array>} Promise resolving to array of capabilities
 */
export const getPersonaCapabilities = async (personaId) => {
  try {
    const response = await axios.get(`${API_URL}/${personaId}/capabilities`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching capabilities for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Update persona capabilities
 * @param {string} personaId - Persona ID
 * @param {Array} capabilities - Updated capabilities
 * @returns {Promise<Object>} Promise resolving to updated persona
 */
export const updatePersonaCapabilities = async (personaId, capabilities) => {
  try {
    const response = await axios.put(`${API_URL}/${personaId}/capabilities`, { capabilities });
    return response.data;
  } catch (error) {
    console.error(`Error updating capabilities for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona knowledge sources
 * @param {string} personaId - Persona ID
 * @returns {Promise<Array>} Promise resolving to array of knowledge sources
 */
export const getPersonaKnowledgeSources = async (personaId) => {
  try {
    const response = await axios.get(`${API_URL}/${personaId}/knowledge-sources`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching knowledge sources for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Update persona knowledge sources
 * @param {string} personaId - Persona ID
 * @param {Array} knowledgeSources - Updated knowledge sources
 * @returns {Promise<Object>} Promise resolving to updated persona
 */
export const updatePersonaKnowledgeSources = async (personaId, knowledgeSources) => {
  try {
    const response = await axios.put(`${API_URL}/${personaId}/knowledge-sources`, { knowledgeSources });
    return response.data;
  } catch (error) {
    console.error(`Error updating knowledge sources for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona performance metrics
 * @param {string} personaId - Persona ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to performance metrics
 */
export const getPersonaPerformance = async (personaId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${personaId}/performance`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching performance for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona conversation history
 * @param {string} personaId - Persona ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of conversations
 */
export const getPersonaConversations = async (personaId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${personaId}/conversations`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching conversations for persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona templates
 * @returns {Promise<Array>} Promise resolving to array of persona templates
 */
export const getPersonaTemplates = async () => {
  try {
    const response = await axios.get(`${API_URL}/templates`);
    return response.data;
  } catch (error) {
    console.error('Error fetching persona templates:', error);
    throw error;
  }
};

/**
 * Create persona from template
 * @param {string} templateId - Template ID
 * @param {Object} personaData - Persona data
 * @returns {Promise<Object>} Promise resolving to created persona
 */
export const createPersonaFromTemplate = async (templateId, personaData) => {
  try {
    const response = await axios.post(`${API_URL}/templates/${templateId}/create`, personaData);
    return response.data;
  } catch (error) {
    console.error(`Error creating persona from template ${templateId}:`, error);
    throw error;
  }
};

/**
 * Clone a persona
 * @param {string} personaId - Persona ID to clone
 * @param {Object} options - Clone options
 * @returns {Promise<Object>} Promise resolving to cloned persona
 */
export const clonePersona = async (personaId, options = {}) => {
  try {
    const response = await axios.post(`${API_URL}/${personaId}/clone`, options);
    return response.data;
  } catch (error) {
    console.error(`Error cloning persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Test a persona
 * @param {string} personaId - Persona ID
 * @param {Object} testData - Test data
 * @returns {Promise<Object>} Promise resolving to test results
 */
export const testPersona = async (personaId, testData) => {
  try {
    const response = await axios.post(`${API_URL}/${personaId}/test`, testData);
    return response.data;
  } catch (error) {
    console.error(`Error testing persona ${personaId}:`, error);
    throw error;
  }
};

/**
 * Get persona categories
 * @returns {Promise<Array>} Promise resolving to array of persona categories
 */
export const getPersonaCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching persona categories:', error);
    throw error;
  }
};

export default {
  getPersonas,
  getPersonaById,
  createPersona,
  updatePersona,
  deletePersona,
  getPersonaCapabilities,
  updatePersonaCapabilities,
  getPersonaKnowledgeSources,
  updatePersonaKnowledgeSources,
  getPersonaPerformance,
  getPersonaConversations,
  getPersonaTemplates,
  createPersonaFromTemplate,
  clonePersona,
  testPersona,
  getPersonaCategories
};

